'use client';
import { useCallback, useState, useEffect } from 'react';
import { useWindowEvent } from '../useWindowEvent/useWindowEvent.js';

function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function serializeJSON(value, hookName) {
    try {
        return JSON.stringify(value);
    } catch (error) {
        throw new Error("@s7/hooks " + hookName + ": Не удалось выполнить сериализацию значения");
    }
}
function deserializeJSON(value) {
    try {
        return JSON.parse(value);
    } catch (e) {
        return value;
    }
}
function createStorage(type, hookName) {
    var eventName = type === "localStorage" ? "ds-local-storage" : "ds-session-storage";
    return function useStorage(param) {
        var key = param.key, _param_defaultValue = param.defaultValue, defaultValue = _param_defaultValue === void 0 ? undefined : _param_defaultValue, _param_getInitialValueInEffect = param.getInitialValueInEffect, getInitialValueInEffect = _param_getInitialValueInEffect === void 0 ? true : _param_getInitialValueInEffect, _param_deserialize = param.deserialize, deserialize = _param_deserialize === void 0 ? deserializeJSON : _param_deserialize, _param_serialize = param.serialize, serialize = _param_serialize === void 0 ? function(value) {
            return serializeJSON(value, hookName);
        } : _param_serialize;
        var readStorageValue = useCallback(function(skipStorage) {
            if (typeof window === "undefined" || !(type in window) || skipStorage) {
                return defaultValue;
            }
            var storageValue = window[type].getItem(key);
            return storageValue !== null ? deserialize(storageValue) : defaultValue;
        }, [
            key,
            defaultValue
        ]);
        var _useState = useState(readStorageValue(getInitialValueInEffect)), value = _useState[0], setValue = _useState[1];
        var setStorageValue = useCallback(function(value) {
            if (_instanceof(value, Function)) {
                setValue(function(current) {
                    var result = value(current);
                    window[type].setItem(key, serialize(result));
                    window.dispatchEvent(new CustomEvent(eventName, {
                        detail: {
                            key: key,
                            value: value(current)
                        }
                    }));
                    return result;
                });
            } else {
                window[type].setItem(key, serialize(value));
                window.dispatchEvent(new CustomEvent(eventName, {
                    detail: {
                        key: key,
                        value: value
                    }
                }));
                setValue(value);
            }
        }, [
            key
        ]);
        var removeStorageValue = useCallback(function() {
            window[type].removeItem(key);
            window.dispatchEvent(new CustomEvent(eventName, {
                detail: {
                    key: key,
                    value: defaultValue
                }
            }));
        }, []);
        useWindowEvent("storage", function(event) {
            if (event.storageArea === window[type] && event.key === key) {
                var _event_newValue;
                setValue(deserialize((_event_newValue = event.newValue) != null ? _event_newValue : undefined));
            }
        });
        useWindowEvent(eventName, function(event) {
            if (event.detail.key === key) {
                setValue(event.detail.value);
            }
        });
        useEffect(function() {
            if (defaultValue !== undefined && value === undefined) {
                setStorageValue(defaultValue);
            }
        }, [
            defaultValue,
            value,
            setStorageValue
        ]);
        useEffect(function() {
            if (getInitialValueInEffect) {
                setStorageValue(readStorageValue());
            }
        }, [
            key
        ]);
        return [
            value === undefined ? defaultValue : value,
            setStorageValue,
            removeStorageValue
        ];
    };
}

export { createStorage };
